import axios from 'axios';
import { API_BASE_URL } from './config';
import { STORAGE_KEYS } from '../constants/storage';
import { APP_PATHS } from '../routes/paths';

let refreshingPromise: Promise<string> | null = null;
let isRedirecting = false;

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;
    return Date.now() >= exp;
  } catch {
    return true;
  }
}

export const TokenManager = {
  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  setTokens(token: string, refreshToken: string): void {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  },

  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.PERMISSIONS);
  },

  isAccessTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;
    return isTokenExpired(token);
  },

  redirectToLogin(): void {
    if (isRedirecting) return;
    isRedirecting = true;
    this.clearTokens();
    window.location.href = APP_PATHS.login;
  },

  async refreshTokens(): Promise<string> {
    if (isRedirecting) {
      return Promise.reject(new Error('Already redirecting to login'));
    }

    if (refreshingPromise) {
      return refreshingPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.redirectToLogin();
      return Promise.reject(new Error('No refresh token available'));
    }

    refreshingPromise = (async () => {
      try {
        const resp = await axios.post(`${API_BASE_URL}/refresh`, {
          refreshToken,
        });

        const data = resp.data?.data || {};
        if (!data.token || !data.refreshToken) {
          throw new Error('Invalid refresh response');
        }

        this.setTokens(data.token, data.refreshToken);
        return data.token;
      } catch (err) {
        this.redirectToLogin();
        throw err;
      } finally {
        refreshingPromise = null;
      }
    })();

    return refreshingPromise;
  },
};
